import React, { useState, useEffect } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { useNavigate } from 'react-router-dom';
import { payments as mockPayments } from '../../data/mockData';
import { paymentApi } from '../../api/paymentApi';
import { isApiConfigured } from '../../api/axiosClient';
import { LoadingSpinner, ErrorCard } from '../../components/LoadingSpinner';

const TABS = ['All', 'Completed', 'Failed', 'Processing', 'Refunded'];

const PaymentHistoryPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');
  const [page, setPage] = useState({ skip: 0, take: 5 });
  const [sort, setSort] = useState([]);
  const [payments, setPayments] = useState(mockPayments);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isApiConfigured()) {
      setLoading(true);
      setError(null);
      paymentApi.getPayments()
        .then((res) => setPayments(res.data))
        .catch((err) => setError(err.message || 'Failed to load payments'))
        .finally(() => setLoading(false));
    }
  }, []);

  /* -- derived stats ------------------------------------------------------ */
  const totalPaid = payments
    .filter((p) => p.status === 'Completed')
    .reduce((sum, p) => sum + p.amount, 0);
  const failedCount = payments.filter((p) => p.status === 'Failed').length;
  const refundedCount = payments.filter((p) => p.status === 'Refunded').length;
  const processingCount = payments.filter(
    (p) => p.status === 'Processing'
  ).length;

  /* -- filtered data ------------------------------------------------------ */
  const filtered =
    activeTab === 'All'
      ? payments
      : payments.filter((p) => p.status === activeTab);

  const sortedData = [...filtered].sort((a, b) => {
    if (!sort.length) return 0;
    const { field, dir } = sort[0];
    const aVal = a[field];
    const bVal = b[field];
    if (aVal < bVal) return dir === 'asc' ? -1 : 1;
    if (aVal > bVal) return dir === 'asc' ? 1 : -1;
    return 0;
  });

  const pagedData = sortedData.slice(page.skip, page.skip + page.take);

  /* -- cell renderers ----------------------------------------------------- */
  const StatusCell = (props) => {
    const status = props.dataItem.status;
    let cls = '';
    if (status === 'Completed') cls = 'paid';
    else if (status === 'Failed') cls = 'denied';
    else if (status === 'Processing') cls = 'pending';
    else if (status === 'Refunded') cls = 'info';
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <span className={`status-badge ${cls}`}>{status}</span>
      </td>
    );
  };

  const AmountCell = (props) => (
    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>${props.dataItem.amount.toFixed(2)}</td>
  );

  const DateCell = (props) => {
    const d = new Date(props.dataItem.date + 'T00:00:00');
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        {d.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        })}
      </td>
    );
  };

  /* -- tab styles --------------------------------------------------------- */
  const getTabStyle = (tab) => ({
    padding: '8px 20px',
    borderRadius: 'var(--radius-full)',
    border: 'none',
    background:
      activeTab === tab
        ? 'linear-gradient(135deg, #0F766E, #0D9488)'
        : 'transparent',
    color: activeTab === tab ? '#fff' : 'var(--text-secondary)',
    fontWeight: activeTab === tab ? 600 : 500,
    fontSize: 13,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    fontFamily: 'inherit',
  });

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Payment History</h1>
          <p>Review all payment transactions and their statuses</p>
        </div>
        <button
          className="btn-secondary"
          onClick={() => navigate('/invoices')}
        >
          Back to Invoices
        </button>
      </div>

      {loading && <LoadingSpinner message="Loading payments..." />}
      {error && <ErrorCard message={error} onRetry={() => window.location.reload()} />}

      {!loading && !error && (
      <div>
      {/* Stat Cards */}
      <div className="stat-cards">
        <div className="stat-card">
          <div className="stat-icon green">{'\u2705'}</div>
          <div className="stat-value">
            ${totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
          <div className="stat-label">Total Paid</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon danger">{'\u274C'}</div>
          <div className="stat-value">{failedCount}</div>
          <div className="stat-label">Failed Attempts</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">{'\u21A9\uFE0F'}</div>
          <div className="stat-value">{refundedCount}</div>
          <div className="stat-label">Refunded</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">{'\u23F3'}</div>
          <div className="stat-value">{processingCount}</div>
          <div className="stat-label">Processing</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          marginBottom: 20,
          background: 'var(--bg-subtle)',
          padding: 6,
          borderRadius: 'var(--radius-full)',
          width: 'fit-content',
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab}
            style={getTabStyle(tab)}
            onClick={() => {
              setActiveTab(tab);
              setPage({ skip: 0, take: 5 });
            }}
          >
            {tab === 'Completed' ? 'Paid' : tab}
          </button>
        ))}
      </div>

      {/* Grid or Empty State */}
      {filtered.length === 0 ? (
        <div
          className="card"
          style={{ textAlign: 'center', padding: '48px 24px' }}
        >
          <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>
            {'\u{1F50D}'}
          </div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: 'var(--text-primary)',
              marginBottom: 4,
            }}
          >
            No payments found
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
            Try a different filter or date range
          </div>
        </div>
      ) : (
        <div className="grid-scroll-wrapper">
          <Grid
            data={pagedData}
            sortable={true}
            sort={sort}
            onSortChange={(e) => setSort(e.sort)}
            pageable={{ pageSizes: false }}
            skip={page.skip}
            take={page.take}
            total={filtered.length}
            onPageChange={(e) =>
              setPage({ skip: e.page.skip, take: e.page.take })
            }
          >
            <GridColumn field="transactionId" title="Transaction ID" width="220px" />
            <GridColumn field="invoiceId" title="Invoice ID" width="120px" />
            <GridColumn field="amount" title="Amount ($)" cells={{ data: AmountCell }} width="130px" />
            <GridColumn field="method" title="Method" width="140px" />
            <GridColumn field="status" title="Status" cells={{ data: StatusCell }} width="160px" />
            <GridColumn field="date" title="Date" cells={{ data: DateCell }} width="140px" />
          </Grid>
        </div>
      )}
      </div>
      )}
    </div>
  );
};

export default PaymentHistoryPage;
